import type { Grade, LevelConfig, RuleId } from './types.js';

/**
 * The built-in curriculum.
 *
 * A sub-level is nothing more than a named `LevelConfig`. The teacher panel
 * edits the same shape, and a teacher's own curriculum (see `curriculum.ts`) is
 * a list of these — so "Grade 3, level 2 but with a 60-second clock and no
 * adjacency" is a saved setting, not a code change.
 */
export interface SubLevel {
  index: number;
  /** Untranslated key; the UI resolves it through i18n. */
  nameKey: string;
  config: LevelConfig;
}

export interface GradeDef {
  grade: Grade;
  rule: RuleId;
  /** i18n key describing the rule to a student, e.g. "pairs that sum to 10". */
  ruleKey: string;
  levels: SubLevel[];
}

export const base: LevelConfig = {
  rule: 'equal',
  ruleTarget: 0,
  digits: 1,
  rows: 4,
  cols: 6,
  adjacency: false,
  timerSec: null,
  adds: 3,
  hints: 3,
  missPenalty: 2,
  guaranteeSolvable: true,
  allowEqualMultiples: false,
  allowOneAsFactor: false,
  maxMultiplier: 0,
  huntDensity: 0.45,
  // Every game reveals a picture as it clears; a teacher can switch it off.
  puzzle: true,
  puzzleScene: '',
  multipleDisplay: 'factor',
  celebration: 'word',
  decorations: true,
};

const lvl = (index: number, nameKey: string, over: Partial<LevelConfig>): SubLevel => ({
  index,
  nameKey,
  config: { ...base, ...over },
});

/** Years 1–2 celebrate loudly; from Year 3 the praise stays out of the way. */
const young = { celebration: 'full' as const };

/**
 * Every board is wider than it is tall. Classrooms show the game on a
 * landscape screen, where a tall board leaves most of the width empty and has
 * to shrink its tiles to fit the height; ten columns is the widest a phone
 * still reads comfortably.
 *
 * Grades 1–2 run without the connectivity constraint: a six-year-old who can
 * see 3 and 7 should never be told "not those two" for a spatial reason they
 * cannot reason about. From Grade 3 the constraint is what makes it a puzzle.
 */
export const GRADES: GradeDef[] = [
  {
    grade: 1,
    rule: 'equal',
    ruleKey: 'rule.equal',
    levels: [
      lvl(1, 'level.gentle', { ...young, rule: 'equal', rows: 3, cols: 6, timerSec: null }),
      lvl(2, 'level.steady', { ...young, rule: 'equal', rows: 4, cols: 5, timerSec: 120 }),
      lvl(3, 'topic.even',   { ...young, rule: 'even', valueMin: 1, valueMax: 20, rows: 4, cols: 5, timerSec: 120, adds: 0 }),
      lvl(4, 'topic.odd',    { ...young, rule: 'odd',  valueMin: 1, valueMax: 20, rows: 4, cols: 5, timerSec: 120, adds: 0 }),
      lvl(5, 'level.brisk',  { ...young, rule: 'equal', rows: 5, cols: 6, timerSec: 100, adds: 2 }),
      lvl(6, 'level.sharp',  { ...young, rule: 'equal', rows: 4, cols: 9, timerSec: 90, adds: 2, hints: 2 }),
    ],
  },
  {
    grade: 2,
    rule: 'sumTo',
    ruleKey: 'rule.sumTo',
    levels: [
      lvl(1, 'level.gentle', { ...young, rule: 'sumTo', ruleTarget: 10, rows: 3, cols: 6, timerSec: null }),
      lvl(2, 'level.steady', { ...young, rule: 'sumTo', ruleTarget: 10, rows: 5, cols: 6, timerSec: 120 }),
      lvl(3, 'topic.mult2',  { ...young, rule: 'multiplesOf', ruleTarget: 2, valueMin: 1, valueMax: 30, rows: 4, cols: 6, timerSec: 120, adds: 0 }),
      lvl(4, 'level.brisk',  { ...young, rule: 'sumTo', ruleTarget: 10, rows: 5, cols: 8, timerSec: 100, adds: 2 }),
      lvl(5, 'level.sharp',  { ...young, rule: 'sumTo', ruleTarget: 10, rows: 5, cols: 10, timerSec: 90, adds: 2, hints: 1 }),
      lvl(6, 'level.fierce', { ...young, rule: 'sumTo', ruleTarget: 10, rows: 6, cols: 10, timerSec: 75, adds: 1, hints: 0 }),
    ],
  },
  {
    grade: 3,
    rule: 'sumTo20',
    ruleKey: 'rule.sumTo20',
    levels: [
      lvl(1, 'level.gentle', { rule: 'sumTo20', ruleTarget: 20, valueMin: 1, valueMax: 19, rows: 4, cols: 6, adjacency: true, timerSec: 150 }),
      lvl(2, 'level.steady', { rule: 'sumTo20', ruleTarget: 20, valueMin: 1, valueMax: 19, rows: 4, cols: 9, adjacency: true, timerSec: 130 }),
      lvl(3, 'topic.multN',  { rule: 'multiplesOf', ruleTarget: 3, valueMin: 1, valueMax: 40, rows: 4, cols: 6, timerSec: 130, adds: 0 }),
      lvl(4, 'level.brisk',  { rule: 'sumTo20', ruleTarget: 20, valueMin: 1, valueMax: 19, rows: 5, cols: 10, adjacency: true, timerSec: 110, adds: 2 }),
      lvl(5, 'level.sharp',  { rule: 'sumTo20', ruleTarget: 20, valueMin: 1, valueMax: 19, rows: 6, cols: 10, adjacency: true, timerSec: 95, adds: 2, hints: 1 }),
    ],
  },
  {
    grade: 4,
    rule: 'multipleOf',
    ruleKey: 'rule.multipleOf',
    levels: [
      lvl(1, 'level.gentle',  { rule: 'multipleOf', valueMin: 2, valueMax: 24, rows: 4, cols: 6, adjacency: true, timerSec: 150 }),
      lvl(2, 'level.steady',  { rule: 'multipleOf', valueMin: 2, valueMax: 36, rows: 4, cols: 9, adjacency: true, timerSec: 130 }),
      lvl(3, 'topic.multN',   { rule: 'multiplesOf', ruleTarget: 4, valueMin: 1, valueMax: 48, rows: 5, cols: 6, timerSec: 130, adds: 0 }),
      lvl(4, 'topic.factors', { rule: 'factorsOf', ruleTarget: 24, valueMin: 1, valueMax: 30, rows: 4, cols: 6, timerSec: 130, adds: 0, huntDensity: 0.4 }),
      lvl(5, 'level.brisk',   { rule: 'multipleOf', valueMin: 2, valueMax: 48, rows: 5, cols: 8, adjacency: true, timerSec: 110, adds: 2 }),
      lvl(6, 'level.sharp',   { rule: 'multipleOf', valueMin: 2, valueMax: 72, rows: 5, cols: 10, adjacency: true, timerSec: 95, adds: 2, hints: 1 }),
    ],
  },
  {
    grade: 5,
    rule: 'sameProduct',
    ruleKey: 'rule.sameProduct',
    levels: [
      lvl(1, 'level.gentle',    { rule: 'sameProduct', rows: 3, cols: 6, adjacency: true, timerSec: 150 }),
      lvl(2, 'topic.product12', { rule: 'productTo', ruleTarget: 12, valueMin: 1, valueMax: 12, rows: 3, cols: 6, adjacency: true, timerSec: 150, allowOneAsFactor: true }),
      lvl(3, 'level.steady',    { rule: 'sameProduct', rows: 4, cols: 6, adjacency: true, timerSec: 140 }),
      lvl(4, 'topic.productN',  { rule: 'productTo', ruleTarget: 24, valueMin: 1, valueMax: 24, rows: 4, cols: 5, adjacency: true, timerSec: 140, allowOneAsFactor: true }),
      lvl(5, 'level.brisk',     { rule: 'sameProduct', rows: 5, cols: 6, adjacency: true, timerSec: 120, adds: 2 }),
      lvl(6, 'level.sharp',     { rule: 'sameProduct', rows: 4, cols: 9, adjacency: true, timerSec: 105, adds: 2, hints: 1 }),
    ],
  },
  {
    grade: 6,
    rule: 'equivalent',
    ruleKey: 'rule.equivalent',
    levels: [
      lvl(1, 'level.gentle',   { rule: 'equivalent', rows: 3, cols: 6, adjacency: true, timerSec: 160 }),
      lvl(2, 'topic.prime',    { rule: 'prime', valueMin: 2, valueMax: 50, rows: 4, cols: 5, timerSec: 150, adds: 0, huntDensity: 0.4 }),
      lvl(3, 'topic.notPrime', { rule: 'notPrime', valueMin: 2, valueMax: 50, rows: 4, cols: 5, timerSec: 150, adds: 0, huntDensity: 0.5 }),
      lvl(4, 'level.steady',   { rule: 'equivalent', rows: 4, cols: 5, adjacency: true, timerSec: 145 }),
      lvl(5, 'level.brisk',    { rule: 'equivalent', rows: 4, cols: 6, adjacency: true, timerSec: 125, adds: 2 }),
      lvl(6, 'level.sharp',    { rule: 'equivalent', rows: 5, cols: 6, adjacency: true, timerSec: 110, adds: 2, hints: 1 }),
    ],
  },
];

export function getGrade(grade: Grade): GradeDef {
  const g = GRADES.find((x) => x.grade === grade);
  if (!g) throw new Error(`Unknown grade ${grade}`);
  return g;
}

export function getLevel(grade: Grade, index: number): SubLevel {
  const g = getGrade(grade);
  const l = g.levels.find((x) => x.index === index) ?? g.levels[0];
  if (!l) throw new Error(`Grade ${grade} has no levels`);
  return l;
}

/** Config for a grade/level, with any teacher overrides layered on top. */
export function resolveConfig(grade: Grade, index: number, overrides?: Partial<LevelConfig>): LevelConfig {
  return { ...getLevel(grade, index).config, ...(overrides ?? {}) };
}

/**
 * The label for a level in a list.
 *
 * A level named after a topic says the topic; one named after a difficulty says
 * the difficulty. Both resolve through i18n, so this only picks the key.
 */
export const levelNameKey = (l: SubLevel): string => l.nameKey;
